import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Loader2, Lock, Mail, User as UserIcon } from "lucide-react";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { useLang } from "@/i18n/LangContext";
import { LocalizedLink, useLocalizedNav } from "@/i18n/LocalizedLink";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function SignUp() {
  const { t } = useLang();
  const { register } = useUserAuth();
  const lnav = useLocalizedNav();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/profile";
  const [form, setForm] = useState({ username: "", email: "", password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error(t("auth.pwd_mismatch"));
      return;
    }
    if (form.password.length < 8) {
      toast.error(t("auth.pwd_too_short"));
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success(t("auth.account_created"));
      lnav(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[80vh] grid place-items-center p-6 relative" data-testid="signup-page">
      <div className="aurora opacity-50" />
      <div className="relative w-full max-w-md py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-brand to-brand-cyan glow-blue mb-4">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">{t("nav.sign_up")}</h1>
          <p className="text-sm text-zinc-400">{t("auth.sign_up_sub")}</p>
        </div>

        <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6 space-y-4">
          <GoogleAuthButton next={next} />

          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-600 font-bold">
            <div className="flex-1 h-px bg-white/5" />
            <span>{t("auth.or")}</span>
            <div className="flex-1 h-px bg-white/5" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <Field icon={UserIcon} label={t("auth.username")} value={form.username} onChange={(v) => setForm({ ...form, username: v })} testid="signup-username" />
            <Field icon={Mail} type="email" label={t("auth.email")} value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="signup-email" />
            <Field icon={Lock} type="password" label={t("auth.password")} value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="signup-password" />
            <Field icon={Lock} type="password" label={t("auth.confirm_password")} value={form.confirm_password} onChange={(v) => setForm({ ...form, confirm_password: v })} testid="signup-confirm" />

            <button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 rounded-lg glow-blue transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="signup-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {t("nav.sign_up")}
            </button>
            <p className="text-center text-sm text-zinc-500 pt-1">
              {t("auth.have_account")} <LocalizedLink to="/signin" className="text-white font-semibold" data-testid="goto-signin-link">{t("nav.sign_in")}</LocalizedLink>
            </p>
          </form>
        </div>
      </div>
    </main>
  );
}

function Field({ icon: Icon, label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2 block">
        <Icon className="w-3 h-3 inline me-1" /> {label}
      </label>
      <input
        required type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#080808] border border-white/10 rounded-lg px-4 py-3 focus:border-brand outline-none transition-colors"
        data-testid={testid}
      />
    </div>
  );
}
